import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { HomePick, HomeResult, HomeStanding, HomeSummary as Summary } from '../api.js';
import TeamBadge from './TeamBadge.js';
import { formatKickoff } from '../lib/intl.js';
import styles from './HomeSummary.module.css';

/**
 * The three cards above the group list on the home screen (PLAN.md 10.27):
 * what needs picking, what has been scored, and where you stand.
 *
 * Each renders nothing at all when it has nothing to say, which is what
 * keeps the screen calm: out of season it collapses back to the plain group
 * list, and a brand-new account never sees an empty dashboard telling it
 * there are zero of everything.
 */
export default function HomeSummary({ summary }: { summary: Summary }) {
  return (
    <div className={styles.sections}>
      <NeedsPicks summary={summary} />
      <RecentResults summary={summary} />
      <Standings standings={summary.standings} />
    </div>
  );
}

/** Ticks a countdown to `target`, coarse enough not to re-render every second. */
function useCountdown(target: string | null): number | null {
  const [remaining, setRemaining] = useState(() =>
    target ? Date.parse(target) - Date.now() : null,
  );

  useEffect(() => {
    if (!target) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(Date.parse(target) - Date.now());
    tick();
    // Half a minute: the label's smallest unit is a minute, so a faster tick
    // would only ever repaint the same string.
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [target]);

  return remaining;
}

function NeedsPicks({ summary }: { summary: Summary }) {
  const { t, i18n } = useTranslation();
  const remaining = useCountdown(summary.nextDeadlineAt);

  if (summary.needsPicks.length === 0) return null;

  const locale = i18n.resolvedLanguage ?? 'en';
  const hidden = summary.needsPicksTotal - summary.needsPicks.length;
  // Under an hour the countdown turns red — the point at which "later" stops
  // being a real option.
  const urgent = remaining !== null && remaining <= 60 * 60 * 1000;

  return (
    <section className={`panel ${styles.section}`}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>
          {t('home.needsPicks', { count: summary.needsPicksTotal })}
        </span>
        {remaining !== null ? (
          <span className={`${styles.countdown} ${urgent ? styles.countdownSoon : ''}`}>
            {formatCountdown(remaining, t)}
          </span>
        ) : null}
      </div>

      <div className={styles.rows}>
        {summary.needsPicks.map((pick) => (
          <PickRow key={`${pick.bolaoId}-${pick.matchId}`} pick={pick} locale={locale} />
        ))}
      </div>

      {hidden > 0 ? (
        <Link
          to={`/groups/${summary.needsPicks[0]!.groupId}/bolaos/${summary.needsPicks[0]!.bolaoId}/predictions`}
          className={styles.more}
        >
          {t('home.andMore', { count: hidden })}
        </Link>
      ) : null}
    </section>
  );
}

function PickRow({ pick, locale }: { pick: HomePick; locale: string }) {
  return (
    <Link to={`/groups/${pick.groupId}/bolaos/${pick.bolaoId}/predictions`} className={styles.row}>
      <span className={styles.side}>
        <TeamBadge team={pick.homeTeam} />
        <span className={styles.teamName}>{pick.homeTeam?.name ?? '?'}</span>
      </span>
      <span className={styles.side}>
        <TeamBadge team={pick.awayTeam} />
        <span className={styles.teamName}>{pick.awayTeam?.name ?? '?'}</span>
      </span>
      <span className={styles.sub}>
        <span className={styles.subText}>{pick.bolaoName}</span>
        <span className={styles.kickoff}>{formatKickoff(pick.kickoffAt, locale)}</span>
      </span>
    </Link>
  );
}

function RecentResults({ summary }: { summary: Summary }) {
  const { t } = useTranslation();
  if (summary.recentResults.length === 0) return null;

  // Titled by what it is actually showing: a genuine catch-up when something
  // is new, an ordinary recap once it has been read.
  const title = summary.newResultCount > 0 ? t('home.sinceLastVisit') : t('home.recentResults');

  return (
    <section className={`panel ${styles.section}`}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{title}</span>
        {summary.newResultCount > 0 ? (
          <span className={styles.newCount}>
            {t('home.newCount', { count: summary.newResultCount })}
          </span>
        ) : null}
      </div>

      <div className={styles.rows}>
        {summary.recentResults.map((result) => (
          <ResultRow key={`${result.bolaoId}-${result.matchId}`} result={result} />
        ))}
      </div>
    </section>
  );
}

function ResultRow({ result }: { result: HomeResult }) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/groups/${result.groupId}/bolaos/${result.bolaoId}/predictions`}
      className={styles.row}
    >
      <span className={styles.side}>
        {result.isNew ? <span className={styles.newDot} /> : null}
        <TeamBadge team={result.homeTeam} />
        <span className={styles.teamName}>{result.homeTeam?.name ?? '?'}</span>
        <span className={styles.goals}>{result.realHome ?? '?'}</span>
      </span>
      <span className={styles.side}>
        <TeamBadge team={result.awayTeam} />
        <span className={styles.teamName}>{result.awayTeam?.name ?? '?'}</span>
        <span className={styles.goals}>{result.realAway ?? '?'}</span>
      </span>
      <span className={styles.sub}>
        <span className={styles.subText}>
          {t('home.yourPick', { home: result.predictedHome, away: result.predictedAway })}
        </span>
        <span className={`${styles.points} ${result.points === 0 ? styles.pointsZero : ''}`}>
          {t('home.pointsEarned', { count: result.points })}
        </span>
      </span>
    </Link>
  );
}

function Standings({ standings }: { standings: HomeStanding[] }) {
  const { t } = useTranslation();
  if (standings.length === 0) return null;

  return (
    <section className={`panel ${styles.section}`}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{t('home.yourStandings')}</span>
      </div>

      <div className={styles.rows}>
        {standings.map((standing) => (
          <StandingRow key={standing.bolaoId} standing={standing} />
        ))}
      </div>
    </section>
  );
}

function StandingRow({ standing }: { standing: HomeStanding }) {
  const { t } = useTranslation();
  // Positive means climbed: a smaller position number is a better one.
  const movement =
    standing.previousPosition === null ? null : standing.previousPosition - standing.position;

  return (
    <Link
      to={`/groups/${standing.groupId}/bolaos/${standing.bolaoId}/leaderboard`}
      className={`${styles.row} ${styles.standingRow}`}
    >
      <span
        className={`${styles.position} ${standing.position === 1 ? styles.positionLeader : ''}`}
      >
        {t('home.positionShort', { position: standing.position })}
      </span>
      <span className={styles.standingMain}>
        <span className={styles.standingName}>{standing.bolaoName}</span>
        <span className={styles.sub} style={{ marginTop: 0 }}>
          <span className={styles.subText}>
            {standing.groupName} · {t('groups.playerCount', { count: standing.participantCount })}
          </span>
        </span>
      </span>
      <span className={styles.standingRight}>
        {movement !== null ? (
          <span
            className={`${styles.movement} ${
              movement > 0
                ? styles.movementUp
                : movement < 0
                  ? styles.movementDown
                  : styles.movementFlat
            }`}
          >
            {movement > 0 ? `▲${movement}` : movement < 0 ? `▼${Math.abs(movement)}` : '–'}
          </span>
        ) : null}
        <span className={styles.total}>{t('home.points', { count: standing.points })}</span>
      </span>
    </Link>
  );
}

/** "2d 4h" / "3h 20m" / "8m" / "now" — coarse on purpose, see `useCountdown`. */
function formatCountdown(remainingMs: number, t: TFunction): string {
  if (remainingMs <= 0) return t('home.closingNow');

  const totalMinutes = Math.floor(remainingMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return t('home.countdownDays', { days, hours });
  if (hours > 0) return t('home.countdownHours', { hours, minutes });
  return t('home.countdownMinutes', { minutes: Math.max(minutes, 1) });
}
